import { usePlanCreatorContext } from "@components/PlanCreator/PlanCreatorContext";
import { formatHhMmWithoutTz } from "@components/PlanCreator/helpers";
import { MODALS_ENUM } from "@constants/Modals";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { show } from "redux-modal";
import { dev } from "@utils/dev";
import { arePeriodsEqual, areRangeEqual, isEmptyPeriod, sortPeriods } from "@components/PlanCreator/Periods.utils";
import { IPlanRange } from "@services/getMinerList";

export const usePeriodOptionsControl = () => {

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const {
    errors,
    disabled,
    plan,
    readOnly,
    periods,
    templates,
    weekDayPeriods,
    selectedDays,
    isCorrectPeriods,
    setPeriods,
    appendTemplates,
  } = usePlanCreatorContext();

  const _saveTemplate = useCallback(async () => {
    if (!plan) return;
    const fromElements = document.querySelectorAll('[id*="from_picker-"]');
    const untilElements = document.querySelectorAll('[id*="until_picker-"]');
    const saved: [string, string][] = [];
    for (let i = 0; i < fromElements.length; i++) {
      saved.push([
        (fromElements[i] as HTMLInputElement).value,
        (untilElements[i] as HTMLInputElement).value,
      ]);
    }
    if (!isCorrectPeriods(saved)) return;
    appendTemplates(plan.id, saved);
  }, [plan, appendTemplates]);

  const onSavePeriodsTemplate = useCallback(() => {
    if (!plan) return;
    const exists = templates.find((tt) => tt.planId === plan?.id);
    if (exists) {
      dispatch(
        show(MODALS_ENUM.CONFIRM_MODAL, {
          title: formatMessage({ id: "manage.plan_info.template.confirm" }),
          onConfirm() {
            _saveTemplate();
          },
        })
      );
      return;
    }
    _saveTemplate();
  }, [templates, plan, _saveTemplate]);

  const [current, setCurrentPeriods] = useState<IPlanRange[]>([]);

  useEffect(() => {
    setCurrentPeriods(periods.filter(p => !isEmptyPeriod(p.time)));
  }, [periods]);

  const [draggedItem, setDraggedItem] = useState<IPlanRange | null>(null);

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    item: IPlanRange,
    idx: number
  ) => {
    setDraggedItem(item);
    e.dataTransfer.setData('text/plain', idx.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetIdx: number) => {
    e.preventDefault();
    if (!draggedItem) return;
    const draggedIdx = Number(e.dataTransfer.getData('text/plain'));
    const newItems = [...current];
    const [removed] = newItems.splice(draggedIdx, 1);
    newItems.splice(targetIdx, 0, removed);
    setCurrentPeriods(newItems);
    setPeriods(newItems);
    setDraggedItem(null);
  };

  const sortCurrentPeriods = useCallback(() => {
    const sorted = sortPeriods(current);
    setPeriods(sorted);
  }, [current]);

  return {
    errors,
    disabled,
    current,
    readOnly,
    selectedDays,
    onSavePeriodsTemplate,
    handleDrop,
    handleDragOver,
    handleDragStart,
    sortCurrentPeriods,
  };
};
